"use strict";
/**
 * 媒体库契约。
 *
 * 上传走**预签名直传**：文件字节不经过我们的服务器。这不是为了省事，
 * 而是 4C8G 单机上最实际的选择 —— 客户传一张 5MB 原图，经服务器中转就是
 * 一次 5MB 入 + 5MB 出，十几个客户同时传就能把出口带宽打满，还占用 Node 的
 * 堆内存（multipart 解析要整块缓冲）。直传之后服务端只负责"签一张限时的、
 * 只能写一个 key 的票据"。
 *
 * 代价是服务端看不到文件内容，所以上传完成后必须回来 **confirm** 一次：
 * 用 HeadObject 核对真实类型与大小，与申报一致才置为 ready。否则客户端可以
 * 申报 image/jpeg，实际传一个 500MB 的压缩包上去。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MEDIA_RETENTION_DAYS = exports.PRESIGN_TTL_SECONDS = exports.MAX_UPLOAD_BYTES = exports.IMAGE_MIME_EXT = exports.MEDIA_MIME = exports.MEDIA_VARIANTS_STATUS = exports.MEDIA_VARIANT_FORMATS = exports.MEDIA_VARIANT_SIZES = void 0;
exports.variantKey = variantKey;
exports.variantName = variantName;
exports.isUploadMime = isUploadMime;
/**
 * 派生图档位。
 *
 * 四档而不是更多：srcset 的收益是“别让手机下 3000px 的图”，
 * 档位一多，解码/上传/存储的耗时按档数线性涨，而实际只会命中其中一两档。
 * 320 给商品缩略图、640 给卡片、1280 给内容区大图、1920 给全宽横幅。
 *
 * 原图本身不进档位表：它已经是最大那一档，但要单独处理
 * （可能是 png/gif，也可能宽高未知），见 MediaDerivative 的组装处。
 */
exports.MEDIA_VARIANT_SIZES = [
    { name: 'thumb', width: 320 },
    { name: 'card', width: 640 },
    { name: 'hero', width: 1280 },
    { name: 'wide', width: 1920 },
];
/**
 * 派生图格式。
 *
 * avif 比 webp 再省 20~30%，但编码慢得多（同一张图约 5~10 倍）。
 * 两个都出、由浏览器在 srcset 里自己挑：现代浏览器优先 avif，
 * 老浏览器回落到 webp，都不支持时才用原图。
 *
 * 不支持 png：png 的价值是无损（图标、截图），而照片类原图转 webp
 * 后体积是 png 的零头，再存一档 png 只是白花存储。
 */
exports.MEDIA_VARIANT_FORMATS = ['avif', 'webp'];
/**
 * 派生图状态。
 *
 * 三个“还没好”的值必须分开，否则无法判断该不该重试：
 * pending 排队中（重试有意义）、failed 试过失败了（提示客户）、
 * skipped 这张图不需要派生（重试没意义，不能当失败无限重试）。
 */
exports.MEDIA_VARIANTS_STATUS = ['pending', 'ready', 'failed', 'skipped'];
/**
 * 原图键 → 派生图键。
 *
 * 把派生图放进以原图 uuid 命名的**目录**（`{uuid}.jpg` → `{uuid}/card.webp`），
 * 而不是平铺成 `{uuid}.card.webp`：前者天然按媒体聚在一起，
 * 将来要按前缀清理某个媒体的一整套派生图只需一次 delete-multi。
 *
 * 键的第二段用 `${size}-${format}`（`card-webp`）作为 variants 对象的字段名，
 * 用连字符而不是点：JSON 里带点会让人下意识当成嵌套路径。
 */
function variantKey(objectKey, size, format) {
    const dot = objectKey.lastIndexOf('.');
    const slash = objectKey.lastIndexOf('/');
    // 只有点出现在最后一段里才是扩展名（目录名理论上可能带点）
    const base = dot > slash ? objectKey.slice(0, dot) : objectKey;
    return `${base}/${size}-${format}.${format}`;
}
/** variants 对象里的字段名。列表与渲染层共用，避免一边写 `card-webp`、一边找 `card.webp`。 */
function variantName(size, format) {
    return `${size}-${format}`;
}
/**
 * 允许上传的类型。
 *
 * SVG 刻意不收：它能内嵌 <script>，客户上传一个"logo"结果浏览器执行了里面的
 * 脚本，这个场景不值得为了兼容性去赌。真要支持，得先做一次净化。
 *
 * GIF 收：外贸站的包装/装配演示图常是动图，且 COS 能直接按原样回源。
 */
exports.MEDIA_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/avif', 'image/gif'];
/** 类型 → 对象键扩展名。扩展名由 mime 决定，**不看文件名**。 */
exports.IMAGE_MIME_EXT = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/avif': 'avif',
    'image/gif': 'gif',
};
/**
 * 单文件上限 10MB。
 *
 * 外贸站的产品图原图通常在 3~8MB（相机直出），10MB 能覆盖绝大多数；
 * 再大就不是"传不上"而是"该压缩了"，拦在这里比事后清理便宜。
 */
exports.MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
/** 票据有效期。10 分钟够慢速网络传完 10MB，又短到泄露了也没多大用。 */
exports.PRESIGN_TTL_SECONDS = 600;
/** 软删除后对象保留天数，之后由清理任务物理删除。 */
exports.MEDIA_RETENTION_DAYS = 30;
function isUploadMime(mime) {
    return Object.prototype.hasOwnProperty.call(exports.IMAGE_MIME_EXT, mime);
}
//# sourceMappingURL=media.js.map